import {
  CheckCircleOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  Progress,
  Row,
  Statistic,
  Space,
  Table,
  Tag,
} from 'antd';
import type { CurrentUser } from '@cell-bank/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatTenantDate } from '../lib/tenant';

type TransactionItem = {
  cellLineCode: string;
};

type Transaction = {
  id: string;
  operationType: string;
  executedAt: string;
  executedBy: string;
  executedByName: string | null;
  items: TransactionItem[];
};
type Sample = {
  status: string;
  expiryDate?: string | null;
};
type SampleListResponse = {
  items: Sample[];
  statusCounts?: Record<string, number>;
};
type CellLineInventoryRank = {
  id: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
  sampleCount: number;
};
type StorageCapacity = {
  totalPositions: number;
  occupiedPositions: number;
  byDevice: Array<{
    id: string;
    code: string;
    nameZh: string | null;
    nameEn: string | null;
    total: number;
    occupied: number;
  }>;
};
type LiquidNitrogenDevice = {
  id: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
  settings: {
    nominalCapacityL: number;
    lowLevelPct: number;
    version: number | null;
  } | null;
  forecast: {
    estimatedLevelPct: number | null;
    estimatedVolumeL: number | null;
    averageDailyLossL: number | null;
    daysUntilLow: number | null;
    estimatedLowAt: string | null;
    confidence: 'INSUFFICIENT' | 'LOW' | 'MEDIUM' | 'HIGH';
    lastFillAt: string | null;
    sampleCount: number;
  } | null;
};
type OverviewStats = {
  registeredUsers: number | null;
  transactionTotal: number | null;
  accessCount: number | null;
  inventoryCellLineCount: number | null;
  cellLineInventoryRanking: CellLineInventoryRank[] | null;
  cellMaterialCount: number | null;
  deviceCount: number | null;
  storageCapacity: StorageCapacity | null;
  liquidNitrogen: { enabled: boolean; devices: LiquidNitrogenDevice[] } | null;
};

export function OverviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sampleStats, setSampleStats] = useState<Record<string, number>>({});
  const [sampleItems, setSampleItems] = useState<Sample[]>([]);
  const [sampleDataReady, setSampleDataReady] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [capacityDeviceIndex, setCapacityDeviceIndex] = useState(-1);
  const [overviewStats, setOverviewStats] = useState<OverviewStats>({
    registeredUsers: null,
    transactionTotal: null,
    accessCount: null,
    inventoryCellLineCount: null,
    cellLineInventoryRanking: null,
    cellMaterialCount: null,
    deviceCount: null,
    storageCapacity: null,
    liquidNitrogen: null,
  });
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    const load = async () => {
      const me = await fetch('/api/v1/auth/me', { credentials: 'include' });
      if (me.status === 401) {
        window.location.assign('/login');
        return;
      }
      if (!me.ok) {
        setUnavailable(true);
        return;
      }
      const user = (await me.json()) as CurrentUser;
      const canReadSamples = user.permissions.includes('sample:read');
      const canReadHistory = user.permissions.includes('audit:read');
      const [samples, history, overview] = await Promise.allSettled([
        canReadSamples
          ? fetch('/api/v1/samples?limit=100', { credentials: 'include' })
          : Promise.resolve(null),
        canReadHistory
          ? fetch('/api/v1/inventory-transactions?limit=5', {
              credentials: 'include',
            })
          : Promise.resolve(null),
        fetch('/api/v1/system/overview', { credentials: 'include' }),
      ]);
      const responses = [samples, history, overview];
      if (
        responses.some(
          (result) =>
            result.status === 'fulfilled' && result.value?.status === 401,
        )
      ) {
        window.location.assign('/login');
        return;
      }
      setUnavailable(responses.some((result) => result.status === 'rejected'));
      if (samples.status === 'fulfilled' && samples.value?.ok) {
        const sampleList = (await samples.value.json()) as SampleListResponse;
        setSampleItems(sampleList.items);
        setSampleDataReady(true);
        setSampleStats(
          sampleList.statusCounts ?? { TOTAL: sampleList.items.length },
        );
      }
      if (history.status === 'fulfilled' && history.value?.ok) {
        setTransactions(
          ((await history.value.json()) as { items: Transaction[] }).items,
        );
      }
      if (overview.status === 'fulfilled' && overview.value?.ok) {
        setOverviewStats((await overview.value.json()) as OverviewStats);
      }
    };
    void load();
  }, []);
  const operationLabel = (value: string) => {
    const key = `transactions.operationTypes.${value}`;
    const translated = t(key);
    return translated === key ? value : translated;
  };
  const capacity = overviewStats.storageCapacity;
  const liquidNitrogen = overviewStats.liquidNitrogen;
  const selectedCapacityDevice =
    capacity && capacityDeviceIndex >= 0
      ? capacity.byDevice[capacityDeviceIndex]
      : undefined;
  const displayedCapacity = selectedCapacityDevice
    ? {
        totalPositions: selectedCapacityDevice.total,
        occupiedPositions: selectedCapacityDevice.occupied,
      }
    : capacity;
  const displayedCapacityLabel = selectedCapacityDevice
    ? selectedCapacityDevice.nameZh ??
      selectedCapacityDevice.nameEn ??
      selectedCapacityDevice.code
    : t('overview.allStorageCapacity');
  const capacityNavigationLabel = selectedCapacityDevice
    ? displayedCapacityLabel
    : t('overview.allStorageCapacity');
  const capacityPercent =
    displayedCapacity && displayedCapacity.totalPositions > 0
      ? Math.round(
          (displayedCapacity.occupiedPositions / displayedCapacity.totalPositions) *
            100,
        )
      : 0;
  const switchCapacityDevice = (direction: -1 | 1) => {
    const deviceCount = capacity?.byDevice.length ?? 0;
    const viewCount = deviceCount + 1;
    if (viewCount <= 1) return;
    setCapacityDeviceIndex((currentIndex) => {
      const currentView = currentIndex + 1;
      return ((currentView + direction + viewCount) % viewCount) - 1;
    });
  };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiryWarningDate = new Date(today);
  expiryWarningDate.setDate(expiryWarningDate.getDate() + 30);
  const activeSamples = sampleItems.filter(
    (sample) => !['CONSUMED', 'DISPOSED', 'MISSING'].includes(sample.status),
  );
  const expiredSamples = activeSamples.filter((sample) => {
    if (!sample.expiryDate) return false;
    const expiryDate = new Date(`${sample.expiryDate}T00:00:00`);
    return !Number.isNaN(expiryDate.valueOf()) && expiryDate < today;
  }).length;
  const expiringSoonSamples = activeSamples.filter((sample) => {
    if (!sample.expiryDate) return false;
    const expiryDate = new Date(`${sample.expiryDate}T00:00:00`);
    return (
      !Number.isNaN(expiryDate.valueOf()) &&
      expiryDate >= today &&
      expiryDate <= expiryWarningDate
    );
  }).length;
  const abnormalSamples =
    (sampleStats.QUARANTINED ?? 0) + (sampleStats.MISSING ?? 0);
  const inventoryWarningTotal =
    expiredSamples + expiringSoonSamples + abnormalSamples;
  const summaryCards = [
    {
      key: 'availableSamples',
      title: t('overview.availableSamples'),
      value: sampleStats.AVAILABLE ?? 0,
      detail: t('overview.summaryDetails.availableSamples'),
    },
    {
      key: 'inventoryCellLines',
      title: t('overview.inventoryCellLines'),
      value: overviewStats.inventoryCellLineCount ?? '-',
      detail: t('overview.summaryDetails.inventoryCellLines'),
    },
    {
      key: 'cellMaterials',
      title: t('overview.cellMaterials'),
      value: overviewStats.cellMaterialCount ?? '-',
      detail: t('overview.summaryDetails.cellMaterials'),
    },
    {
      key: 'devices',
      title: t('overview.devices'),
      value: overviewStats.deviceCount ?? '-',
      detail: t('overview.summaryDetails.devices'),
    },
  ];
  const cellLineRanking = overviewStats.cellLineInventoryRanking ?? [];
  const maxCellLineCount = cellLineRanking[0]?.sampleCount ?? 0;

  return (
    <section className="overview-page">
      {unavailable && (
        <Alert
          type="warning"
          showIcon
          message={t('overview.partialData')}
          style={{ marginBottom: 16 }}
        />
      )}
      <Row gutter={[16, 16]} className="overview-summary-row">
        {summaryCards.map((card) => (
          <Col key={card.key} xs={24} sm={12} lg={6}>
            <Card className="overview-summary-card" bordered={false}>
              <div className="overview-summary-label">
                <span>{card.title}</span>
                <InfoCircleOutlined />
              </div>
              <div className="overview-summary-value">{card.value}</div>
              <div className="overview-summary-detail">{card.detail}</div>
            </Card>
          </Col>
        ))}
      </Row>
      <Row gutter={[16, 16]} className="overview-workspace-row">
        {capacity && (
          <Col xs={24} lg={8}>
            <Card title={t('overview.storageCapacity')}>
              <div className="overview-capacity-display">
                <div
                  className="overview-capacity-gauge"
                  role="img"
                  aria-label={`${displayedCapacityLabel} ${capacityPercent}%`}
                >
                  <Progress
                    type="dashboard"
                    size={150}
                    percent={capacityPercent}
                    strokeColor="#1677ff"
                    format={(percent) => `${percent}%`}
                  />
                  <span className="overview-capacity-occupied">
                    {displayedCapacity?.occupiedPositions ?? 0} /{' '}
                    {displayedCapacity?.totalPositions ?? 0}
                  </span>
                </div>
              </div>
              {capacity && capacity.byDevice.length > 0 && (
                <div className="overview-capacity-navigation">
                  <span className="overview-capacity-navigation-label">
                    {capacityNavigationLabel}
                  </span>
                  <div className="overview-capacity-pagination">
                    <Button
                      type="text"
                      size="small"
                      icon={<LeftOutlined />}
                      aria-label={t('overview.previousCapacityView')}
                      title={t('overview.previousCapacityView')}
                      onClick={() => switchCapacityDevice(-1)}
                    />
                    <nav aria-label={t('overview.capacityNavigation')}>
                      <div className="overview-capacity-dots">
                      {capacity.byDevice.map((device, index) => {
                        const label =
                          device.nameZh ?? device.nameEn ?? device.code;
                        return (
                          <button
                            key={device.id}
                            type="button"
                            className={`overview-capacity-dot${
                              index === capacityDeviceIndex ? ' is-active' : ''
                            }`}
                            aria-label={label}
                            aria-current={
                              index === capacityDeviceIndex ? 'page' : undefined
                            }
                            title={label}
                            onClick={() => setCapacityDeviceIndex(index)}
                          >
                            <span aria-hidden="true" />
                          </button>
                        );
                        })}
                      </div>
                    </nav>
                    <Button
                      type="text"
                      size="small"
                      icon={<RightOutlined />}
                      aria-label={t('overview.nextCapacityView')}
                      title={t('overview.nextCapacityView')}
                      onClick={() => switchCapacityDevice(1)}
                    />
                  </div>
                </div>
              )}
            </Card>
          </Col>
        )}
        <Col xs={24} lg={16}>
          <Card
            title={t('overview.cellLineInventoryRanking')}
            extra={
              <span className="overview-card-caption">
                {t('overview.cellLineInventoryRankingDetail')}
              </span>
            }
          >
            {overviewStats.cellLineInventoryRanking === null ? (
              <div className="overview-ranking-empty">
                {t('overview.cellLineInventoryRankingUnavailable')}
              </div>
            ) : cellLineRanking.length === 0 ? (
              <div className="overview-ranking-empty">
                {t('overview.cellLineInventoryRankingEmpty')}
              </div>
            ) : (
              <div className="overview-cell-line-ranking">
                {cellLineRanking.map((item, index) => {
                  const name = item.nameZh ?? item.nameEn ?? item.code;
                  const percent =
                    maxCellLineCount > 0
                      ? Math.round((item.sampleCount / maxCellLineCount) * 100)
                      : 0;
                  return (
                    <div className="overview-cell-line-ranking-item" key={item.id}>
                      <div className="overview-cell-line-ranking-label">
                        <span className="overview-cell-line-ranking-index">
                          {index + 1}
                        </span>
                        <span className="overview-cell-line-ranking-name" title={name}>
                          {name}
                        </span>
                        <span className="overview-cell-line-ranking-count">
                          {t('overview.cellLineInventoryRankingCount', {
                            count: item.sampleCount,
                          })}
                        </span>
                      </div>
                      <Progress percent={percent} showInfo={false} size="small" />
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </Col>
      </Row>
      {liquidNitrogen?.enabled && liquidNitrogen.devices.length > 0 && (
        <Row gutter={[16, 16]} className="overview-workspace-row">
          <Col xs={24}>
            <Card
              title={t('overview.liquidNitrogenForecast')}
              extra={<Tag color="blue">{t('overview.estimatedData')}</Tag>}
            >
              <div className="overview-liquid-nitrogen-grid">
                {liquidNitrogen.devices.map((device) => {
                  const name =
                    device.nameZh ?? device.nameEn ?? device.code;
                  const level = device.forecast?.estimatedLevelPct ?? null;
                  const isLow =
                    level !== null &&
                    device.settings !== null &&
                    level <= device.settings.lowLevelPct;
                  return (
                    <div
                      className="overview-liquid-nitrogen-item"
                      key={device.id}
                    >
                      <div className="overview-liquid-nitrogen-header">
                        <span title={name}>{name}</span>
                        <strong>
                          {level === null ? '-' : `${level}%`}
                        </strong>
                      </div>
                      <Progress
                        percent={level ?? 0}
                        showInfo={false}
                        status={level === null ? 'normal' : isLow ? 'exception' : 'normal'}
                      />
                      <div className="overview-liquid-nitrogen-detail">
                        {level === null
                          ? t('overview.liquidNitrogenInsufficientData')
                          : `${device.forecast?.estimatedVolumeL ?? 0} L · ${t(
                              'overview.liquidNitrogenDaysUntilLow',
                              {
                                days: device.forecast?.daysUntilLow ?? '-',
                              },
                            )}`}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </Col>
        </Row>
      )}
      <Row gutter={[16, 16]} className="overview-workspace-row">
        <Col xs={24}>
          <Card title={t('overview.inventoryWarnings')}>
            <Space size="large" align="center" wrap>
              <Statistic
                title={t('overview.expiredSamples')}
                value={sampleDataReady ? expiredSamples : '-'}
                valueStyle={{
                  color: sampleDataReady && expiredSamples ? '#cf1322' : undefined,
                }}
              />
              <Statistic
                title={t('overview.expiringSoonSamples')}
                value={sampleDataReady ? expiringSoonSamples : '-'}
                valueStyle={{
                  color:
                    sampleDataReady && expiringSoonSamples
                      ? '#d48806'
                      : undefined,
                }}
              />
              <Statistic
                title={t('overview.abnormalSamples')}
                value={sampleDataReady ? abnormalSamples : '-'}
                valueStyle={{
                  color: sampleDataReady && abnormalSamples ? '#d46b08' : undefined,
                }}
              />
            </Space>
            {sampleDataReady && inventoryWarningTotal === 0 && (
              <div className="overview-all-clear">
                <CheckCircleOutlined /> {t('overview.inventoryAllClear')}
              </div>
            )}
          </Card>
        </Col>
      </Row>
      <Row gutter={[16, 16]} className="overview-workspace-row">
        <Col xs={24}>
          <Card title={t('overview.recentTransactions')} extra={<Button type="link" onClick={() => void navigate('/transactions')}>{t('overview.viewAll')}</Button>}>
            <Table
              size="small"
              dataSource={transactions}
              rowKey="id"
              pagination={false}
              locale={{ emptyText: t('overview.empty') }}
              columns={[
                {
                  title: t('overview.operation'),
                  dataIndex: 'operationType',
                  render: (value: string) => (
                    <Tag color="blue">{operationLabel(value)}</Tag>
                  ),
                },
                {
                  title: t('overview.executedAt'),
                  dataIndex: 'executedAt',
                  render: (value: string) => formatTenantDate(value),
                },
                {
                  title: t('overview.operator'),
                  dataIndex: 'executedByName',
                  render: (_, row: Transaction) =>
                    row.executedByName ?? row.executedBy,
                },
                {
                  title: t('overview.cellLine'),
                  dataIndex: 'items',
                  render: (_, row: Transaction) => {
                    const cellLines = [
                      ...new Set(row.items.map((item) => item.cellLineCode)),
                    ];
                    const visible = cellLines.slice(0, 3).join('、');
                    return cellLines.length > 3
                      ? `${visible} …`
                      : visible || '-';
                  },
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </section>
  );
}
